import { Controller } from "@hotwired/stimulus"

// Manages checkout form interactions:
// - Toggle display/edit modes for address and payment sections
// - Address radio selection (show/hide new address form)
// - Payment method selection (updates hidden fields)
export default class extends Controller {
  static targets = ["paymentMethodId", "gatewayPaymentMethod", "tosCheckbox", "completeButton"]

  // Generic toggle between display and edit modes within a section.
  // Works with any element containing [data-address-display]/[data-address-edit]
  // or [data-checkout-display]/[data-checkout-edit] siblings.
  toggle(event) {
    event.preventDefault()
    const section = event.currentTarget.closest("[data-address-section]") || this.element
    const pairs = [
      [section.querySelector("[data-address-display]"), section.querySelector("[data-address-edit]")],
      [section.querySelector("[data-checkout-display]"), section.querySelector("[data-checkout-edit]")]
    ]
    for (const [display, edit] of pairs) {
      if (display && edit) {
        display.classList.toggle("hidden")
        edit.classList.toggle("hidden")
      }
    }
  }

  // Show/hide inline new-address form based on radio selection.
  // Enables fields when "new" is selected, disables when an existing address is chosen.
  addressRadioChanged(event) {
    const section = event.target.closest("[data-address-section]")
    if (!section) return

    const form = section.querySelector("[data-new-address-form]")
    const fieldset = section.querySelector("[data-new-address-fieldset]")
    if (!form) return

    const isNew = event.target.value === "new"
    form.classList.toggle("hidden", !isNew)
    if (fieldset) fieldset.disabled = !isNew
  }

  // Parse composite payment radio value ("id:gateway_method") into hidden fields.
  updatePaymentMethod(event) {
    const [methodId, gatewayMethod] = event.target.value.split(":")
    if (this.hasPaymentMethodIdTarget) this.paymentMethodIdTarget.value = methodId
    if (this.hasGatewayPaymentMethodTarget) this.gatewayPaymentMethodTarget.value = gatewayMethod || ""
  }

  toggleCompleteButton() {
    if (!this.hasTosCheckboxTarget || !this.hasCompleteButtonTarget) return

    this.completeButtonTarget.disabled = !this.tosCheckboxTarget.checked
  }

  disableSubmit(event) {
    const form = event.currentTarget
    if (form.dataset.submitting === "true") {
      event.preventDefault()
      return
    }

    form.dataset.submitting = "true"
    const submitter = event.submitter || form.querySelector("[type='submit']")
    if (!submitter) return

    const disableWith = submitter.dataset.disableWith
    submitter.disabled = true
    if (disableWith) submitter.textContent = disableWith
  }

  // Reset open edit sections before Turbo caches the page snapshot.
  connect() {
    this.toggleCompleteButton()

    this._beforeCache = () => {
      for (const edit of this.element.querySelectorAll("[data-address-edit]:not(.hidden)")) {
        edit.classList.add("hidden")
        const section = edit.closest("[data-address-section]") || this.element
        section.querySelector("[data-address-display]")?.classList.remove("hidden")
      }
    }
    document.addEventListener("turbo:before-cache", this._beforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this._beforeCache)
  }
}
